/**
 * Vue i18n 扩展工具类
 * 中文反向查找、智能翻译
 */
import { useI18n } from 'vue-i18n'

/**
 * LRU缓存
 */
class LRUCache<K, V> {
  private maxSize: number
  private cache: Map<K, V>

  constructor(maxSize: number = 1000) {
    this.maxSize = maxSize
    this.cache = new Map()
  }

  get(key: K): V | undefined {
    if (this.cache.has(key)) {
      const value = this.cache.get(key)!
      this.cache.delete(key)
      this.cache.set(key, value)
      return value
    }
    return undefined
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      if (firstKey !== undefined) {
        this.cache.delete(firstKey)
      }
    }
    this.cache.set(key, value)
  }

  has(key: K): boolean {
    return this.cache.has(key)
  }

  clear(): void {
    this.cache.clear()
  }

  get size(): number {
    return this.cache.size
  }
}

/**
 * i18n 扩展工具类
 */
class I18nHelper {
  private static searchCache = new LRUCache<string, string | null>(1000)

  /**
   * 递归查找i18n key
   */
  private static findKeyByValue(
    obj: Record<string, any>,
    targetValue: string,
    currentPath = '',
    visited = new Set<object>()
  ): string | null {
    if (visited.has(obj)) {
      return null
    }
    visited.add(obj)

    try {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = currentPath ? `${currentPath}.${key}` : key

        if (typeof value === 'string' && value === targetValue) {
          return fullPath
        }

        if (typeof value === 'object' && value !== null) {
          const found = I18nHelper.findKeyByValue(value, targetValue, fullPath, visited)
          if (found) return found
        }
      }
    } catch (error) {
      console.warn('Error during i18n key search:', error)
    } finally {
      visited.delete(obj)
    }

    return null
  }

  /**
   * 中文反向查找i18n key
   */
  private static findI18nKeyByText(chineseText: string, locale = 'zh-CN'): string | null {
    if (!chineseText || typeof chineseText !== 'string') {
      return null
    }

    const cacheKey = `${locale}:${chineseText}`
    if (I18nHelper.searchCache.has(cacheKey)) {
      return I18nHelper.searchCache.get(cacheKey) || null
    }

    try {
      const i18nInstance = useI18n()
      const localeMessages = i18nInstance.messages.value[locale]

      if (!localeMessages) {
        console.warn(`Locale ${locale} not found in i18n messages`)
        I18nHelper.searchCache.set(cacheKey, null)
        return null
      }

      const result = I18nHelper.findKeyByValue(localeMessages, chineseText)
      I18nHelper.searchCache.set(cacheKey, result)

      return result
    } catch (error) {
      console.warn('Error accessing i18n instance:', error)
      return null
    }
  }

  /**
   * 智能翻译：中文/key自动识别，根据语言环境返回对应翻译
   */
  static translateByChineseKey(chineseText: string, fallback?: string): string {
    if (!chineseText || typeof chineseText !== 'string') {
      return fallback || ''
    }

    try {
      const { t, locale } = useI18n()

      // i18n key格式：包含.且不包含中文
      if (chineseText.includes('.') && !/[\u4e00-\u9fa5]/.test(chineseText)) {
        const result = t(chineseText, chineseText)
        return result !== chineseText ? result : (fallback || chineseText)
      }

      // 中文反向查找key
      const foundKey = I18nHelper.findI18nKeyByText(chineseText, 'zh-CN')
      if (foundKey) {
        const result = t(foundKey, chineseText)
        return result !== chineseText ? result : (fallback || chineseText)
      }

      // 未找到key时的处理
      if (locale.value === 'zh-CN') {
        return chineseText
      } else {
        return fallback || chineseText
      }
    } catch (error) {
      console.warn('Error in translateByChineseKey:', error)
      return fallback || chineseText || ''
    }
  }

  /**
   * 清除缓存
   */
  static clearCache(): void {
    I18nHelper.searchCache.clear()
  }

  /**
   * 缓存状态
   */
  static getCacheInfo() {
    return {
      size: I18nHelper.searchCache.size,
      maxSize: 1000
    }
  }
}

// 导出API
export const translateByChineseKey = I18nHelper.translateByChineseKey
export const clearI18nCache = I18nHelper.clearCache
export const getCacheInfo = I18nHelper.getCacheInfo

export default I18nHelper

// 向后兼容
export const useTextByChineseKey = I18nHelper.translateByChineseKey